import React, {
    FC,
    ForwardRefExoticComponent,
    RefAttributes,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { INTL_APP_CODE, RU_APP_CODE, RU_APP_CODE_COUNTRIES } from '../../common/constants';
import { FormFields, TFormField, TRouteType, GeoSuggestion } from '../../common/types';
import { convertToBase64 } from '../../common/utils';
import { useConfig } from '../../hooks/useConfig';
import getI18N from '../../lib/i18n';
import { showCode } from '../../store/form';
import { formSelector, languageSelector, tariffSelector } from '../../store/form/selectors';
import { updateField, updateCoords } from '../../store/form/thunks';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { MetrikaGoals, reachGoal } from '../../utils/metrika';
import Button from '../Button/Button';
import Checkbox from '../Checkbox/Checkbox';
import DropdownSelect from '../DropdownSelect/DropdownSelect';
import FileSelect from '../FileSelect/FileSelect';
import defaultGeoSuggest, { GeoSuggestProps } from '../GeoSuggest/GeoSuggest';
import Input from '../Input/Input';
import Modal from '../Modal/Modal';
import RadioGroup from '../RadioGroup/RadioGroup';
import StyleSelect from '../StyleSelect/StyleSelect';
import Tooltip from '../Tooltip/Tooltip';
import styles from './WidgetForm.module.scss';
import {
    useBadgeTitleOptions,
    useRouteTypeOptions,
    useSubtitleOptions,
    useTariffOptions,
} from './hooks';

const i18n = getI18N();

interface WidgetFormProps {
    GeoSuggest?: ForwardRefExoticComponent<GeoSuggestProps & RefAttributes<HTMLInputElement>>;
}

const defaultProps = {
    GeoSuggest: defaultGeoSuggest,
};

const WidgetForm: FC<WidgetFormProps> = ({ GeoSuggest = defaultGeoSuggest }) => {
    // Библиотеки
    const dispatch = useAppDispatch();
    // Константы
    const REQUIRED_MESSAGE = i18n('Это поле обязательно');
    // Redux
    const { fields, generatedQRImage } = useAppSelector(formSelector);
    const { type, routeType } = fields;
    // Ref's
    const imageInputRef = useRef<HTMLInputElement>(null);
    // Computed
    const tariffOptions = useTariffOptions();
    const badgeTitleOptions = useBadgeTitleOptions(fields);
    const routeTypeOptions = useRouteTypeOptions();
    const subtitleOptions = useSubtitleOptions();
    // State
    const [badgeTitleText, setBadgeTitleText] = useState(fields.titleFixed);

    const styleSelectorVisible = type === 'badge' || type === 'button';
    const routeTypeVisible = ['badge', 'link', 'qr'].includes(type);
    const priceCheckboxVisible = type === 'card' || (type === 'link' && routeType !== 'none');

    const addressVisible = !(['badge', 'link', 'qr'].includes(type) && routeType === 'none');

    const config = useConfig();

    // Hook-form
    const {
        register,
        control,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
        resetField: defaultResetField,
        clearErrors,
    } = useForm<FormFields>({
        defaultValues: {
            ...fields,
            title: i18n('Вызвать такси'),
            address: fields.address,
        },
    });

    useEffect(() => {
        if (fields.app) {
            setValue('address', fields.address);
        }
    }, [fields.app, setValue, fields.address]);

    // Callbacks
    const resetField = useCallback(
        (name: keyof FormFields, options: { defaultValue: string }) => {
            defaultResetField(name, options);
            dispatch(updateField(name, ''));
        },
        [defaultResetField, dispatch]
    );

    const downloadImage = useCallback(() => {
        const element = document.createElement('a');
        element.setAttribute('href', generatedQRImage);
        element.setAttribute('download', 'qr.png');
        document.body.appendChild(element);
        element.click();
        element.remove();
    }, [generatedQRImage]);

    const [modalIsOpen, setModalIsOpen] = useState(false);

    const onSubmit = useCallback(() => {
        if (tariffOptions.length) {
            if (type !== 'qr') dispatch(showCode());
            else downloadImage();
            reachGoal(MetrikaGoals.getWigetCode);
        }
    }, [dispatch, downloadImage, type, tariffOptions]);

    const handleFormChange = useCallback(
        (event: React.ChangeEvent<HTMLFormElement>) =>
            dispatch(
                updateField(
                    event.target.name as unknown as TFormField,
                    event.target.type === 'checkbox' ? event.target.checked : event.target.value
                )
            ),
        [dispatch]
    );

    const onFileChange = useCallback(async () => {
        if (!imageInputRef?.current?.files) return;
        const selectedFile = imageInputRef.current.files[0];
        const base64 = (await convertToBase64(selectedFile)) as string;
        dispatch(updateField('image', base64));
        dispatch(updateField('imageName', selectedFile.name));
    }, [dispatch]);

    const onFileRemove = () => {
        dispatch(updateField('imageName', ''));
        dispatch(updateField('image', ''));
    };
    const lang = useAppSelector(languageSelector);
    const activeTariff = useAppSelector(tariffSelector);

    // Watch
    const isPriceShown = watch('isPriceShown');
    const address = watch('address');

    const isPriceBlockVisible =
        (isPriceShown && priceCheckboxVisible) ||
        (type === 'button' && fields.subtitle === 'price');
    const freeTitleVisible = ['button', 'card'].includes(type) || isPriceBlockVisible;

    // Effects
    useEffect(() => {
        const badgeOption = badgeTitleOptions.find((e) =>
            fields.titleFixed ? e.value === fields.titleFixed : e.value === i18n('Вызвать такси')
        );
        if (badgeOption) {
            setBadgeTitleText(badgeOption.text);
            dispatch(updateField('titleFixed', badgeOption.text));
        } else {
            dispatch(updateField('titleFixed', badgeTitleOptions[0].value));
        }
    }, [fields.titleFixed, badgeTitleOptions, dispatch]);

    useEffect(() => {
        dispatch(updateField('title', i18n('Вызвать такси')));
    }, [dispatch, lang]);
    useEffect(() => {
        setValue('tariff', activeTariff);
    }, [activeTariff, setValue]);
    useEffect(() => {
        setValue('clid', fields.clid);
    }, [fields.clid, setValue]);
    useEffect(() => {
        dispatch(
            updateField(
                'address',
                fields.address ? fields.address : config.localisation.defaultAddress
            )
        );
        dispatch(
            updateField(
                'addressShort',
                fields.addressShort ? fields.addressShort : config.localisation.defaultAddress
            )
        );
        dispatch(
            updateField(
                'validAddress',
                fields.validAddress ? fields.validAddress : config.localisation.defaultAddress
            )
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields.app, fields.lang]);
    // onChange
    const storeOnChange = useCallback(
        (fieldName: keyof FormFields) => (value: string) => {
            setValue(fieldName, value);
            dispatch(updateField(fieldName, value));
        },
        [dispatch, setValue]
    );
    const onStyleChange = useMemo(() => storeOnChange('style'), [storeOnChange]);
    const onAddressChange = useMemo(() => storeOnChange('address'), [storeOnChange]);
    const onTariffChange = useMemo(() => storeOnChange('tariff'), [storeOnChange]);
    const onTitleFixedChange = useMemo(() => storeOnChange('titleFixed'), [storeOnChange]);
    const onRoutetypeChange = useCallback(
        (value: string) => setValue('routeType', value as TRouteType),
        [setValue]
    );

    const searchParams = useSearchParams();
    const setParam = searchParams[1];
    const onValidAddressChange = useCallback(
        (e: GeoSuggestion) => {
            const newAddress = e?.name ?? '';
            const newAddressShort = e?.shortName ?? '';
            const app = RU_APP_CODE_COUNTRIES.includes(e.country) ? RU_APP_CODE : INTL_APP_CODE;
            if (app === INTL_APP_CODE && type === 'card') {
                setModalIsOpen(true);
                dispatch(updateField('type', 'button'));
                setParam({ type: 'button' });
            }
            dispatch(updateField('app', app));
            dispatch(updateCoords({ lon: e?.point[0] ?? '', lat: e.point[1] ?? '' }));
            dispatch(updateField('address', newAddress));
            dispatch(updateField('validAddress', newAddress));
            dispatch(updateField('addressShort', newAddressShort));
            setValue('address', newAddress, { shouldDirty: true });
        },
        [dispatch, setValue, type, setModalIsOpen, setParam]
    );
    const onPriceShownChange = useCallback(
        (value: boolean) => setValue('isPriceShown', value),
        [setValue]
    );
    const addressOptions = useMemo(
        () => ({
            validate: {
                validAddress: (v: string) => {
                    if (!v) return REQUIRED_MESSAGE;
                    if (!fields.validAddress || fields.validAddress !== v)
                        return i18n('Неправильный адрес');
                    return true;
                },
            },
        }),
        [REQUIRED_MESSAGE, fields.validAddress]
    );

    const onModalClose = useCallback(() => setModalIsOpen(false), [setModalIsOpen]);

    return (
        <>
            <form
                onSubmit={handleSubmit(onSubmit)}
                onChange={handleFormChange}
                className={styles.form}
            >
                <div className={styles.container}>
                    {styleSelectorVisible && (
                        <Controller
                            control={control}
                            name="style"
                            render={({ field }) => (
                                <StyleSelect
                                    label={i18n('Стиль') as string}
                                    {...field}
                                    onChange={onStyleChange}
                                />
                            )}
                        />
                    )}
                    {routeTypeVisible && (
                        <Controller
                            name="routeType"
                            control={control}
                            render={({ field }) => (
                                <RadioGroup
                                    options={routeTypeOptions}
                                    label={i18n('Маршрут, встроенный в ссылку')}
                                    value={field.value}
                                    name={field.name}
                                    onChange={onRoutetypeChange}
                                />
                            )}
                        />
                    )}
                    {addressVisible && (
                        <GeoSuggest
                            clearErrors={clearErrors}
                            label={i18n('Адрес')}
                            placeholder={i18n('Адрес')}
                            {...register('address', addressOptions)}
                            value={address}
                            error={errors.address?.message}
                            onValidAddressChange={onValidAddressChange}
                            onChange={onAddressChange}
                            lang={fields.lang}
                        />
                    )}
                    <Controller
                        name="tariff"
                        control={control}
                        render={({ field }) => (
                            <DropdownSelect
                                emptyError={i18n(
                                    'Невозможно выбрать тариф: регион не поддерживается'
                                )}
                                label={i18n('Тариф') as string}
                                options={tariffOptions}
                                active={field.value}
                                onChange={onTariffChange}
                                text={
                                    tariffOptions?.find((e) => e.value === field.value)?.text ??
                                    '---'
                                }
                            />
                        )}
                    />
                    {type === 'card' && (
                        <div>
                            <Controller
                                name="image"
                                control={control}
                                render={({ field }) => (
                                    <FileSelect
                                        ref={imageInputRef}
                                        label={i18n('Фоновое изображение') as string}
                                        value={field.value}
                                        onChange={onFileChange}
                                    />
                                )}
                            />
                            {fields.imageName && (
                                <div className={styles.remove}>
                                    <span>{fields.imageName}</span>
                                    <Button
                                        onClick={onFileRemove}
                                        icon="trash"
                                        variant="justicon"
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    {priceCheckboxVisible && (
                        <Controller
                            name="isPriceShown"
                            control={control}
                            render={({ field }) => (
                                <Checkbox
                                    name={field.name}
                                    label={i18n('Стоимость')}
                                    checked={field.value}
                                    onChange={onPriceShownChange}
                                />
                            )}
                        />
                    )}
                    {freeTitleVisible && (
                        <Controller
                            name="title"
                            control={control}
                            rules={{ required: { value: true, message: REQUIRED_MESSAGE } }}
                            render={({ field }) => (
                                <Input
                                    label={i18n('Заголовок')}
                                    placeholder={i18n('Вызвать такси')}
                                    error={errors.title?.message}
                                    resetField={resetField}
                                    {...field}
                                />
                            )}
                        />
                    )}
                    {type === 'badge' && (
                        <Controller
                            name="titleFixed"
                            control={control}
                            render={({ field }) => (
                                <DropdownSelect
                                    label={i18n('Заголовок')}
                                    active={field.value}
                                    text={badgeTitleText}
                                    onChange={onTitleFixedChange}
                                    options={badgeTitleOptions}
                                />
                            )}
                        />
                    )}
                    {type === 'button' && (
                        <Controller
                            name="subtitle"
                            control={control}
                            render={({ field }) => (
                                <RadioGroup
                                    options={subtitleOptions}
                                    label={i18n('Подзаголовок') as string}
                                    {...field}
                                />
                            )}
                        />
                    )}
                    {isPriceBlockVisible && (
                        <div>
                            <p className={styles['small-text']}>
                                {i18n(
                                    'Введите авторизационные данные для доступа к API. Получить их можно'
                                )}{' '}
                                <a href={config.taxiApiUrl} target="blank" className={styles.link}>
                                    {i18n('оставив заявку')}
                                </a>
                                .
                            </p>
                            <div className={styles.row2x}>
                                <Controller
                                    name="priceClid"
                                    control={control}
                                    rules={{ required: { value: true, message: REQUIRED_MESSAGE } }}
                                    render={({ field }) => (
                                        <Input
                                            placeholder="CLID API"
                                            error={errors.priceClid?.message}
                                            resetField={resetField}
                                            {...field}
                                        />
                                    )}
                                />
                                <Controller
                                    name="priceKey"
                                    control={control}
                                    rules={{ required: { value: true, message: REQUIRED_MESSAGE } }}
                                    render={({ field }) => (
                                        <Input
                                            placeholder="KEY"
                                            error={errors.priceKey?.message}
                                            resetField={resetField}
                                            {...field}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    )}
                    <Controller
                        name="clid"
                        control={control}
                        rules={{ required: { value: true, message: REQUIRED_MESSAGE } }}
                        render={({ field }) => (
                            <Input
                                placeholder={
                                    i18n('Адрес сайта или CLID партнерской программы') as string
                                }
                                error={errors.clid?.message}
                                resetField={resetField}
                                {...field}
                            />
                        )}
                    />
                    {fields.app === RU_APP_CODE && (
                        <div className={styles['erid-container']}>
                            <Controller
                                name="erid"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        placeholder={i18n('ERID') as string}
                                        className={styles.grow}
                                        resetField={resetField}
                                        {...field}
                                    />
                                )}
                            />
                            <Tooltip text={i18n('widgetgenerator.erip.tooltip')} />
                        </div>
                    )}
                </div>
                <div className={styles.spacer} />
                <div className={styles.footer}>
                    <p className={styles['micro-text']}>
                        {i18n('Нажимая «Получить код» вы соглашаетесь с')}{' '}
                        <a
                            href={config.localisation.legalUrl}
                            target="blank"
                            className={styles.link}
                        >
                            {i18n('условиями использования')}
                        </a>
                    </p>
                    <Button
                        type="submit"
                        className={styles.button}
                        text={type === 'qr' ? i18n('Скачать QR код') : i18n('Получить код')}
                    />
                </div>
            </form>
            <Modal isOpen={modalIsOpen} onClose={onModalClose}>
                <p>{i18n('widgetgenerator.modal.message')}</p>
            </Modal>
        </>
    );
};

WidgetForm.defaultProps = defaultProps;

export default WidgetForm;
